import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames, { Argument } from 'classnames';

import { GET_MEDIA_UPLOAD_URL } from 'constants/url';

import { AbstractMedia, MediaType } from 'types/AbstractMedia';
import { MediaEntityType, MediaSubType } from 'types/MediaTypeEnum';

import useUpload from 'hooks/useUpload';

import { ButtonType } from '../Button';
import { useSelector } from 'react-redux';
import { Button, Carousel, Icon, Spinner } from 'components/ui';
import { getVehicleCheckImageSupport, getVehicleCheckVideoSupport } from 'modules/refData/selectors';

export interface CardProps {
  selfServiceId: string;
  onUploaded: (uploadedMedia: AbstractMedia) => void;
  className?: Argument;
  hidePreview?: boolean;
  defaultMedia?: AbstractMedia;
  onDelete?: () => void;
}

const MediaCardUpload: React.FC<CardProps> = ({
  selfServiceId, defaultMedia, onUploaded, onDelete, hidePreview, className,
}) => {
  const intl = useIntl();
  const {
    browse, upload, isError, isLoading,
  } = useUpload({ selfServiceId });

  const supportedImageTypes = useSelector(getVehicleCheckImageSupport);
  const supportedVideoTypes = useSelector(getVehicleCheckVideoSupport);

  const [file, setFile] = React.useState<File>();
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [media, setMedia] = React.useState<AbstractMedia>(defaultMedia);

  const { type, uploadId } = media ?? {};

  const isUploaded = !isError && Boolean(uploadId);
  const uploadIcon = type === MediaType.VIDEO ? 'fb-icon--camera-video' : 'fb-icon--camera';

  const displayErrorStatus = !isLoading && isError;
  const displayCheckStatus = !isLoading && isUploaded;
  const displayStatus = isLoading || isUploaded || isError;
  const displayUploadIcon = !isLoading && !isError && !isUploaded;

  const handleUpload = React.useCallback(async (fileParam: File = undefined) => {
    let newFile = fileParam;

    try {
      // If no file was provided, we browse for one
      if (!newFile) {
        const files = await browse({
          accept: defaultMedia?.type === MediaType.VIDEO ? supportedVideoTypes : supportedImageTypes,
          capture: 'environment',
        });

        if (files.length > 0) {
          // Neither of the two writings are valid for this line 🤷‍♀️

          newFile = files[0];
          setFile(newFile);
        }
      }

      if (newFile) {
        const newMedia = await upload({
          file: newFile,
          filename: newFile.name,
          contentType: newFile.type,
          url: GET_MEDIA_UPLOAD_URL,
          subType: MediaSubType.ITEM_RESPONSE_MEDIA,
          entityType: MediaEntityType.VEHICLE_CHECK,
        });

        setMedia(newMedia);
        onUploaded(newMedia);
      }
    } catch {
      // Nothing to do
    }
  }, [browse, defaultMedia?.type, supportedVideoTypes, supportedImageTypes, upload, onUploaded]);

  const handleOpen = React.useCallback(() => setIsFullscreen(true), []);
  const handleClose = React.useCallback(() => setIsFullscreen(false), []);
  // Good luck finding the right type to avoid creating this ugly wrapper
  const handleUploadOnClick = React.useCallback(() => handleUpload(), [handleUpload]);
  const handleRetry = React.useCallback(() => handleUpload(file), [file, handleUpload]);
  const handleDelete = React.useCallback(() => {
    setMedia(defaultMedia);
    onDelete?.();
  }, [defaultMedia, onDelete]);

  return (
    <div className="relative rounded-card overflow-hidden">
      <Carousel
        medias={[media]}
        onClose={handleClose}
        fullscreen={isFullscreen}
        className={className}
      />
      <div className={classNames(
        'absolute flex top-0 left-0 size-full z-20',
        { 'justify-end items-end': isUploaded, 'justify-center items-center': !isUploaded },
      )}
      >
        {displayUploadIcon && (
          <Button
            type={ButtonType.ICON}
            onClick={handleUploadOnClick}
            className={classNames(
              'size-14 !m-2 rounded-button cursor-pointer bg-white flex items-center justify-center',
              isLoading ? 'p-4' : 'p-3',
            )}
            testId="upload"
            aria-label={intl.formatMessage({ id: 'accessibility.button.upload', defaultMessage: 'Upload a new media' })}
          >
            {isLoading ? <Spinner /> : <Icon className={classNames(uploadIcon, 'text-3xl text-default')}/>}
          </Button>
        )}
        {isError && (
          <div className="flex flex-col items-center text-center text-white uppercase font-bold">
            <FormattedMessage id="error.media.upload.retry" defaultMessage="Upload fail, retry" />
            <div
              className="flex justify-center items-center size-14 p-3 !m-2 rounded-button cursor-pointer bg-white"
              data-testid="retry"
              onClick={handleRetry}
            >
              {isLoading ? <Spinner /> : <Icon className="fb-icon--refresh text-3xl text-default" />}
            </div>
          </div>
        )}
        {!hidePreview && (
          <Button
            type={ButtonType.ICON}
            className="flex justify-center items-center size-14 p-3 !m-2 rounded-button cursor-pointer bg-white"
            onClick={handleOpen}
            testId="preview"
            aria-label={intl.formatMessage({ id: 'accessibility.button.viewMedia', defaultMessage: 'View the media' })}
          >
            <Icon className="fb-icon--eye text-3xl text-default" />
          </Button>
        )}
        {isUploaded && (
          <Button
            type={ButtonType.ICON}
            className="flex justify-center items-center size-14 p-3 !m-2 rounded-button cursor-pointer bg-white"
            onClick={handleDelete}
            testId="delete"
            aria-label={intl.formatMessage({ id: 'accessibility.button.deleteMedia', defaultMessage: 'Delete the media' })}
          >
            <Icon className="fb-icon--delete text-3xl text-danger-default" />
          </Button>
        )}
      </div>
      {!isUploaded && (
        <div
          className="absolute flex justify-center top-0 left-0 size-full bg-brand-high opacity-60 z-10 rounded-card"
        />
      )}
      {displayStatus && (
        <div className={classNames(
          'absolute top-0 right-0 size-0 border-solid z-30 border-0 border-r-60 border-b-60 border-transparent border-r-success-default',
          { 'border-r-warning-default': isLoading, 'border-r-danger-default': isError },
        )}
        >
          {isLoading && <Icon className="fb-icon--upload-process text-2xl ml-7 mt-1 block text-white"/>}
          {displayErrorStatus && <Icon className="fb-icon--warning text-2xl ml-7 mt-1 block text-white"/>}
          {displayCheckStatus && <Icon className="fb-icon--check text-2xl ml-7 mt-1 block text-white"/>}
        </div>
      )}
    </div>
  );
};

export default MediaCardUpload;
